import React from 'react';
import {
  Drawer,
  DrawerContainer,
  DrawerContent,
} from '@noths/polaris-client-ribbons-design-system';
import { css } from 'styled-components';

import type { RejectionLines } from '../types';
import * as S from '../PhotoPicker.styles';

const PANEL_CONTENT_STYLES = css`
  max-width: 450px;
  background: #ffffff;
  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.1);
`;

interface Props {
  cancelLabel: string;
  children: React.ReactNode;
  confirmDisabled?: boolean;
  confirmLabel: string;
  describedById?: string;
  heading: string;
  isOpen: boolean;
  max?: number;
  onCancel: () => void;
  onConfirm: () => void;
  /** Called on overlay click / Esc. Distinct from `onCancel` so callers can
   * implement smart-dismiss (e.g. auto-confirm) without affecting the
   * Cancel button. */
  onDismiss: () => void;
  /** Customer-facing rejection lines supplied by the consumer. Primary entries
   * render with full emphasis; sub-details render indented beneath them. */
  rejectionLines?: RejectionLines;
  requirementsLines?: string[];
  subDescription: string;
  /** Notice rendered above the footer when set — used to prompt the
   * customer to confirm or cancel pending edits. */
  unsavedChangesMessage?: string;
  /** Upload/presign failure message surfaced alongside rejection lines so
   * all drawer-level errors share one styled location. */
  uploadErrorMessage?: string;
}

export function PhotoPickerDrawer({
  cancelLabel,
  children,
  confirmDisabled,
  confirmLabel,
  describedById,
  heading,
  isOpen,
  max,
  onCancel,
  onConfirm,
  onDismiss,
  rejectionLines,
  requirementsLines,
  subDescription,
  unsavedChangesMessage,
  uploadErrorMessage,
}: Props) {
  const visibleRejectionLines =
    rejectionLines && (rejectionLines.primary.length > 0 || rejectionLines.subDetails.length > 0)
      ? rejectionLines
      : null;
  const showErrorList = !!visibleRejectionLines || !!uploadErrorMessage;
  return (
    <Drawer
      contentContainerStyles={PANEL_CONTENT_STYLES}
      contentLabel={heading}
      describedBy={describedById}
      isOpen={isOpen}
      onOverlayClick={onDismiss}
      onRequestClose={onDismiss}
      transitionFrom="right"
    >
      <DrawerContainer>
        <DrawerContent>
          <S.DrawerBody>
            <S.DrawerOptionContainer>
              <S.DrawerHeading>{heading}</S.DrawerHeading>
              <S.DrawerSubDescription>
                {subDescription}
                {max != null ? ` (Max ${max})` : ''}
              </S.DrawerSubDescription>
              {requirementsLines && requirementsLines.length > 0 && (
                <S.DrawerRequirements>
                  {requirementsLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </S.DrawerRequirements>
              )}
              {showErrorList && (
                <S.RejectionList>
                  {uploadErrorMessage && (
                    <S.RejectionLine role="alert">{uploadErrorMessage}</S.RejectionLine>
                  )}
                  {visibleRejectionLines?.primary.map((message) => (
                    <S.RejectionLine key={`primary-${message}`} role="alert">
                      {message}
                    </S.RejectionLine>
                  ))}
                  {visibleRejectionLines?.subDetails.map((message) => (
                    <S.RejectionLine $indented key={`sub-${message}`} role="alert">
                      {message}
                    </S.RejectionLine>
                  ))}
                </S.RejectionList>
              )}
            </S.DrawerOptionContainer>
            {children}
          </S.DrawerBody>
        </DrawerContent>
        {unsavedChangesMessage && (
          <S.UnsavedChangesBanner role="alert">{unsavedChangesMessage}</S.UnsavedChangesBanner>
        )}
        <S.Footer>
          <S.FooterButton $variant="secondary" onClick={onCancel} type="button">
            {cancelLabel}
          </S.FooterButton>
          <S.FooterButton
            $variant="primary"
            disabled={confirmDisabled}
            onClick={onConfirm}
            type="button"
          >
            {confirmLabel}
          </S.FooterButton>
        </S.Footer>
      </DrawerContainer>
    </Drawer>
  );
}
